import { SyntaxNode, Tree, TreeCursor } from "@lezer/common";
import { type AutoCompleteEntry, CompletionItemKind } from "@sourceacademy/common-autocomplete";

import linkedListJSON from "./builtins/linked_list.json";
import listJSON from "./builtins/list.json";
import mathJSON from "./builtins/math.json";
import miscJSON from "./builtins/misc.json";
import pairmutatorJSON from "./builtins/pairmutator.json";
import streamJSON from "./builtins/stream.json";
import { getKeywords } from "./keywords";

type Environment = {
  variables: string[];
  functions: string[];
  child: Environment | null;
};

const getNodeText = (node: SyntaxNode, doc: string): string => {
  return doc.slice(node.from, node.to);
};

const getTopLevelImportNames = (topNode: SyntaxNode, doc: string): string[] => {
  const names: string[] = [];

  for (let statement = topNode.firstChild; statement; statement = statement.nextSibling) {
    if (statement.type.name !== "ImportStatement") {
      continue;
    }

    const parts: SyntaxNode[] = [];
    for (let part = statement.firstChild; part; part = part.nextSibling) {
      parts.push(part);
    }

    const importIndex = parts.findIndex(part => part.type.name === "import");
    if (importIndex === -1) {
      continue;
    }

    const isFromImport = parts[0]?.type.name === "from";
    let firstName: string | undefined;
    let alias: string | undefined;
    let readingAlias = false;

    const addBinding = () => {
      const name = alias ?? firstName;
      if (name !== undefined) {
        names.push(name);
      }
      firstName = undefined;
      alias = undefined;
      readingAlias = false;
    };

    for (const part of parts.slice(importIndex + 1)) {
      if (part.type.name === ",") {
        addBinding();
      } else if (part.type.name === "as") {
        readingAlias = true;
      } else if (part.type.name === "VariableName") {
        const name = getNodeText(part, doc);
        if (readingAlias) {
          alias = name;
          readingAlias = false;
        } else if (firstName === undefined) {
          firstName = name;
        } else if (isFromImport) {
          addBinding();
          firstName = name;
        }
      }
    }
    addBinding();
  }

  return names;
};

function isCompletionItemKind(value: string): value is CompletionItemKind {
  return Object.values(CompletionItemKind).includes(value as CompletionItemKind);
}

/**
 * The extractEnvironment function traverses the syntax tree from the given position downwards to collect variable and function names in scope.
 * It returns an Environment object representing the current scope and its child scope unless the position is inside a function parameter list, in which case it returns null.
 *
 * @param iter The TreeCursor used to traverse the syntax tree
 * @param pos The 0-based index position in the document for which to extract the environment
 * @param doc The document text, used to extract variable and function names from the syntax nodes
 * @returns The Environment object representing the current scope and its child scopes, or null if the position is inside a function parameter list
 */
const extractEnvironment = (
  iter: TreeCursor,
  topNode: SyntaxNode,
  pos: number,
  doc: string,
): Environment | null => {
  const topEnv: Environment = {
    variables: [],
    functions: [],
    child: null,
  };

  let currentEnv = topEnv;
  do {
    if (iter.node.type.name === "ParamList") {
      // If the position is inside a function parameter list,
      // we don't want to suggest any names
      return null;
    }
    if (iter.node.type.name === "ForStatement") {
      // Add loop variable to current environment
      const target = iter.node.getChild("VariableName");
      if (target) {
        currentEnv.variables.push(getNodeText(target, doc));
      }
    }
    if (iter.node.type.name == "FunctionDefinition" || iter.node.type.name == "LambdaExpression") {
      // Add function parameters to inner environment
      const params = iter.node.getChild("ParamList");
      if (params) {
        for (let param = params.firstChild; param; param = param.nextSibling) {
          if (param.type.name === "VariableName") {
            currentEnv.variables.push(getNodeText(param, doc));
          }
        }
      }
      continue;
    }
    if (iter.node.type.name !== "Body" && iter.node.type.name !== "Script") {
      continue;
    }
    if (iter.node.type.name === "Script") {
      currentEnv.variables.push(...getTopLevelImportNames(topNode, doc));
    }
    // Iterate children
    for (let child = iter.node.firstChild; child; child = child.nextSibling) {
      // Assignment → variable
      if (child.type.name === "AssignStatement") {
        const left = child.firstChild;
        if (
          left &&
          left.type.name === "VariableName" &&
          (left.from != iter.node.from || left.to != iter.node.to)
        ) {
          currentEnv.variables.push(getNodeText(left, doc));
        }
      }

      // Function
      if (child.type.name === "FunctionDefinition") {
        const nameNode = child.getChild("VariableName");
        if (nameNode) {
          currentEnv.functions.push(getNodeText(nameNode, doc));
        }
      }
    }
    const nextEnv = {
      variables: [],
      functions: [],
      child: null,
    };
    currentEnv.child = nextEnv;
    currentEnv = nextEnv;
  } while (iter.enter(pos, -1));

  // If the node is a function name or loop variable, we don't want to include it in the autocomplete suggestions since it's not in scope at the cursor position. We check for this case by looking at the parent node of the current position - if it's a FunctionDefinition or ForStatement, we return null to indicate that no environment should be extracted.
  if (
    iter.node.parent &&
    ["FunctionDefinition", "ForStatement"].includes(iter.node.parent.type.name)
  ) {
    return null;
  }
  return topEnv;
};

/**
 * Checks if `sub` is a subsequence of `str`, meaning all characters of `sub` appear in `str` in the same order, but not necessarily contiguously.
 * @param sub The subsequence to check
 * @param str The string to check against
 * @returns true if `sub` is a subsequence of `str`, false otherwise
 */
const isSubsequence = (sub: string, str: string): boolean => {
  let i = 0;
  for (const char of str) {
    if (char === sub[i]) {
      i++;
    }
    if (i === sub.length) {
      return true;
    }
  }
  return false;
};

/**
 * Converts a 1-based line and 0-based column number to a 0-based index in the document string. If the line or column is out of bounds,
 * it returns the closest valid index (e.g. end of line or end of document).
 *
 * @param doc The document text
 * @param line The 1-based line number
 * @param column The 0-based column number
 * @returns The 0-based index in the document string corresponding to the given line and column, or the closest valid index if out of bounds
 */
const convertPosToIndex = (doc: string, line: number, column: number): number => {
  let pos = 0;
  while (line > 0) {
    const newlineIndex = doc.indexOf("\n", pos);
    if (newlineIndex === -1) {
      return doc.length;
    }
    pos = newlineIndex + 1;
    line--;
  }
  const lineEnd = doc.indexOf("\n", pos);
  const limit = lineEnd === -1 ? doc.length : lineEnd;
  return Math.min(pos + column, limit);
};

/**
 * Gets the names of variables and functions in scope at the given position in the document,
 * as well as built-in functions, variables and keywords.
 * @param tree The syntax tree of the document, created using Lezer
 * @param doc The document text
 * @param line The 1-based line number of the cursor position
 * @param column The 1-based column number of the cursor position
 * @param variant The variant of the language
 * @returns a list of autocomplete entries, each containing the name, type (variable or function), and documentation (for built-ins)
 */
export const getNames = (
  tree: Tree,
  doc: string,
  line: number,
  column: number,
  variant: number,
): AutoCompleteEntry[] => {
  const pos = convertPosToIndex(doc, line - 1, column); // Convert position to 0-based index
  const node = tree.resolve(pos, -1); // Get the syntax node ending at the cursor position
  if (node.type.name !== "VariableName") {
    return [];
  }

  const query = getNodeText(node, doc);

  let env: Environment | null = extractEnvironment(tree.cursor(), tree.topNode, pos, doc);
  if (env === null) {
    return [];
  }
  const entries: AutoCompleteEntry[] = [];
  let score = 1; // The score is used to prioritize suggestions from inner scopes over outer scopes. Built-ins will have the lowest score.
  while (env) {
    const symbols = [
      ...env.variables.map(v => ({ name: v, meta: CompletionItemKind.Variable, score: score })),
      ...env.functions.map(f => ({ name: f, meta: CompletionItemKind.Function, score: score })),
    ];
    // Filter symbols based on query, sort alphabetically, and add to entries
    symbols
      .filter(s => isSubsequence(query, s.name))
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(s => entries.push(s));

    env = env.child;
    score++;
  }

  getKeywords(variant)
    .map(k => ({
      name: k,
      meta: CompletionItemKind.Keyword,
      score: score, // Keywords are given the highest score
    }))
    .filter(s => isSubsequence(query, s.name))
    .forEach(s => entries.push(s));

  // TODO: Add docstrings for user-defined functions to autocomplete suggestions?
  const symbols = [...miscJSON, ...mathJSON];
  if (variant >= 2) {
    symbols.push(...linkedListJSON);
  }
  if (variant >= 3) {
    symbols.push({
      name: "range",
      title: "range(<CODE>start</CODE>, [<CODE>stop</CODE>], [<CODE>step</CODE>]) -> range",
      description:
        "PRIMITIVE\nUsed with the <CODE>for</CODE> statement to create a loop that iterates a specific number of times. If given one argument, it iterates from 0 to that number (exclusive). If given two arguments, it iterates from the first (inclusive) to the second (exclusive). If given three arguments, it iterates from the first to the second in steps of the third.",
      meta: "func",
    });
    symbols.push(...listJSON);
    symbols.push(...pairmutatorJSON);
    symbols.push(...streamJSON);
  }
  // TODO: add when MCE documentation is pushed into main
  // if (variant >= 4) {
  //   symbols.push(...mceJSON);
  // }
  symbols
    .map(v => ({
      name: v.name,
      meta: isCompletionItemKind(v.meta) ? v.meta : CompletionItemKind.Variable,
      docHTML: "<h4>" + v.title + "</h4><p>" + v.description + "</p>",
    }))
    .filter(s => isSubsequence(query, s.name))
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach(s => entries.push(s));

  return entries;
};
